import { useState, type ReactNode } from "react";
import type { DashboardDateValue, DashboardMetric } from "./dashboardWidgets";
import {
    DashboardDateSelect,
    DataWrap,
    MetricGrid,
    TrendBars,
    useDashboardQuery,
    WidgetBody
} from "./dashboardWidgets";

const DATE_SCOPE_VALUES: Record<string, DashboardDateValue> = { all: 0, month: 2, quarter: 3, year: 4 };

const getInitialDateValue = (active: string) => DATE_SCOPE_VALUES[active] ?? 0;

/** 根看板统计叶子组件的统一 API 数据容器。 */
export function DashboardApiMetricWidget<T extends Record<string, any>>({
    title,
    load,
    metrics,
    options = ["all", "month", "quarter", "year"],
    defaultActive = "all"
}: {
    title: string;
    load: (value: DashboardDateValue) => Promise<T>;
    metrics: Array<{ label: string; key: string; unit?: string }>;
    options?: string[];
    defaultActive?: string;
}) {
    const [time, setTime] = useState<DashboardDateValue>(getInitialDateValue(defaultActive));
    const { data, loading, error } = useDashboardQuery<T>(() => load(time), [time]);
    const result: DashboardMetric[] = metrics.map(metric => ({
        label: metric.label,
        value: data?.[metric.key],
        unit: metric.unit
    }));
    return (
        <DataWrap
            title={title}
            option={
                <DashboardDateSelect value={time} onChange={setTime} options={options} defaultActive={defaultActive} />
            }
        >
            <WidgetBody loading={loading} error={error}>
                <MetricGrid metrics={result} />
            </WidgetBody>
        </DataWrap>
    );
}

/** 根看板趋势叶子组件，保留旧版数组字段和日期切换请求。 */
export function DashboardApiTrendWidget<T extends Record<string, any>>({
    title,
    load,
    series
}: {
    title: string;
    load: (value: DashboardDateValue) => Promise<T>;
    series: Array<{ label: string; key: string }>;
}) {
    const [time, setTime] = useState<DashboardDateValue>(2);
    const { data, loading, error } = useDashboardQuery<T>(() => load(time), [time]);
    return (
        <DataWrap
            title={title}
            option={
                <DashboardDateSelect
                    value={time}
                    onChange={setTime}
                    options={["month", "quarter", "year"]}
                    defaultActive="month"
                />
            }
        >
            <WidgetBody loading={loading} error={error}>
                <div className="space-y-4">
                    {series.map(item => (
                        <div key={item.key}>
                            <div className="mb-2 text-sm text-[#666]">{item.label}</div>
                            <TrendBars values={Array.isArray(data?.[item.key]) ? data?.[item.key] : []} />
                        </div>
                    ))}
                </div>
            </WidgetBody>
        </DataWrap>
    );
}

/** 根看板日期入口，供历史统计组件独立复用。 */
export function DashboardStatisticsDate({
    value,
    onChange
}: {
    value?: DashboardDateValue;
    onChange?: (value: DashboardDateValue) => void;
}) {
    return (
        <DashboardDateSelect
            value={value}
            onChange={next => onChange?.(next)}
            options={["all", "month", "quarter", "year"]}
            defaultActive="all"
        />
    );
}

const formatRate = (value: unknown, total: unknown) => {
    const ratio = Number(total) > 0 ? (Number(value || 0) / Number(total)) * 100 : 0;
    return `${Number.isInteger(ratio) ? ratio : ratio.toFixed(2)}%`;
};

/** 用户漏斗叶子组件，复刻旧 Vue 的三层 SVG 漏斗和右侧转化图例。 */
export function DashboardUserFunnel({ data = {} }: { data?: Record<string, any> }) {
    const registerTotal = Number(data.totalRegisterNum || 0);
    const personalTotal = Number(data.totalPersonalCertificationNum || 0);
    const organizationTotal = Number(data.totalOrganizationCertificationNum || 0);
    const personalRate = formatRate(personalTotal, registerTotal);
    const organizationRate = formatRate(organizationTotal, registerTotal);

    return (
        <div className="funnel-chart-container">
            <div className="funnel-chart-content">
                <svg
                    className="funnel-chart-svg"
                    width="311"
                    height="273"
                    viewBox="0 0 311 274"
                    aria-label="注册用户转化漏斗"
                >
                    <defs>
                        <linearGradient
                            id="dashboard-funnel-yellow"
                            x1="156"
                            y1="147"
                            x2="156"
                            y2="265"
                            gradientUnits="userSpaceOnUse"
                        >
                            <stop stopColor="#fff" />
                            <stop offset="1" stopColor="#fff1c7" />
                        </linearGradient>
                        <linearGradient
                            id="dashboard-funnel-green"
                            x1="155"
                            y1="39"
                            x2="155"
                            y2="214"
                            gradientUnits="userSpaceOnUse"
                        >
                            <stop stopColor="#fff" />
                            <stop offset="1" stopColor="#cdf5b8" />
                        </linearGradient>
                        <linearGradient
                            id="dashboard-funnel-green-line"
                            x1="157"
                            y1="181"
                            x2="157"
                            y2="122"
                            gradientUnits="userSpaceOnUse"
                        >
                            <stop offset=".43" stopColor="#65c730" />
                            <stop offset="1" stopColor="#fff" stopOpacity="0" />
                        </linearGradient>
                        <linearGradient
                            id="dashboard-funnel-yellow-line"
                            x1="159"
                            y1="273"
                            x2="159"
                            y2="229"
                            gradientUnits="userSpaceOnUse"
                        >
                            <stop offset=".43" stopColor="#efcb5f" />
                            <stop offset="1" stopColor="#fff" stopOpacity="0" />
                        </linearGradient>
                    </defs>
                    <path
                        opacity=".5"
                        d="M40 156c6.5 15 54.6 24.8 115.2 24.8 61.1 0 110-11.3 115.2-24.8l-26.9 92.3c-1.6 9.9-26.4 23.3-88.3 24.7-61.9 1.5-82.9-16.2-85.8-24.7L40 156Z"
                        fill="url(#dashboard-funnel-yellow)"
                    />
                    <path
                        opacity=".5"
                        d="M0 38.6c9.4 17.3 58.6 37.3 154.5 37.3 97 0 147.2-21.3 156.5-37.3l-39.8 118.2c-2.1 9.9-34.7 22.3-116.7 23.7-82.1 1.5-110.2-15.2-114-23.7L0 38.6Z"
                        fill="url(#dashboard-funnel-green)"
                    />
                    <ellipse cx="155" cy="39" rx="154" ry="38" fill="#e4f1ff" stroke="#0e88ff" strokeWidth="1.84" />
                    <ellipse
                        cx="156"
                        cy="156"
                        rx="115"
                        ry="25"
                        fill="none"
                        stroke="url(#dashboard-funnel-green-line)"
                        strokeWidth="1.84"
                    />
                    <ellipse
                        cx="158"
                        cy="254"
                        rx="83"
                        ry="18"
                        fill="none"
                        stroke="url(#dashboard-funnel-yellow-line)"
                        strokeWidth="1.84"
                    />
                    <text x="161.5" y="47" textAnchor="end" fill="#0e88ff" fontSize="24" fontWeight="500">
                        {registerTotal} 人
                    </text>
                    <text x="161.5" y="163" textAnchor="end" fill="#65c730" fontSize="24" fontWeight="500">
                        {personalTotal} 人
                    </text>
                    <text x="178.5" y="161" textAnchor="start" fill="#000000a6" fontSize="16" fontWeight="500">
                        {personalRate}
                    </text>
                    <text x="161.5" y="261" textAnchor="end" fill="#ffbf00" fontSize="24" fontWeight="500">
                        {organizationTotal} 人
                    </text>
                    <text x="178.5" y="259" textAnchor="start" fill="#000000a6" fontSize="16" fontWeight="500">
                        {organizationRate}
                    </text>
                </svg>
                <div className="funnel-legend-list">
                    {[
                        { className: "registered-user", name: "累计注册用户" },
                        { className: "personal-verification", name: "累计个人认证", rate: personalRate },
                        { className: "organizational-certification", name: "累计组织机构认证", rate: organizationRate }
                    ].map(item => (
                        <div className={`funnel-legend-item ${item.className}`} key={item.className}>
                            <div className="funnel-legend-name-box">
                                <div className="funnel-legend-name">{item.name}</div>
                                {item.rate ? <div className="funnel-legend-rate">转化率：{item.rate}</div> : null}
                            </div>
                            <div className="funnel-legend-dot" />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

/** 保留插槽能力的看板内容区，用于历史组合入口中的自定义统计内容。 */
export function DashboardLeafPanel({ title, children }: { title: string; children?: ReactNode }) {
    return (
        <DataWrap title={title}>
            {children || <div className="py-8 text-center text-sm text-[#999]">暂无数据</div>}
        </DataWrap>
    );
}

/** 费用分布图，用可点击的横向条替代旧版环形图，保留分组和金额占比。 */
export function DashboardDistribution({
    groups
}: {
    groups: Array<{ title: string; items: Array<{ label: string; value: unknown }> }>;
}) {
    const [selected, setSelected] = useState<string>();
    return (
        <div className="grid gap-5 lg:grid-cols-3">
            {groups.map(group => {
                const values = group.items.map(item => Number(item.value) || 0);
                const total = values.reduce((sum, value) => sum + value, 0);
                return (
                    <div key={group.title} className="rounded-lg border border-[#edf0f5] p-4">
                        <div className="mb-4 text-sm font-medium text-[#333]">{group.title}</div>
                        <div className="space-y-3">
                            {group.items.map((item, index) => {
                                const value = Number(item.value) || 0;
                                const ratio = total > 0 ? (value / total) * 100 : 0;
                                const selectionKey = `${group.title}-${item.label}`;
                                return (
                                    <button
                                        key={item.label}
                                        type="button"
                                        aria-pressed={selected === selectionKey}
                                        className={`w-full rounded p-1 text-left transition ${selected === selectionKey ? "bg-[#f0f5ff]" : ""}`}
                                        title={`${item.label} ${value.toLocaleString("zh-CN")} 万元`}
                                        onClick={() => setSelected(selectionKey)}
                                    >
                                        <div className="mb-1 flex justify-between gap-2 text-xs text-[#666]">
                                            <span>{item.label}</span>
                                            <span>{value.toLocaleString("zh-CN")} 万元</span>
                                        </div>
                                        <div className="h-2 overflow-hidden rounded bg-[#f0f2f5]">
                                            <div
                                                className={`h-full rounded ${index % 2 === 0 ? "bg-[#3370ff]" : "bg-[#6fd195]"}`}
                                                style={{ width: `${Math.max(ratio, value > 0 ? 2 : 0)}%` }}
                                            />
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
